import * as React from "react";
import { css } from "@emotion/core";
import { Button, ButtonGroup, Icon, Tooltip } from "@blueprintjs/core";
import { type DropzoneState } from "react-dropzone";
import { ImagesMachineContext } from "components/Orders/state/imagesMachine";
import { LidarMachineContext } from "components/Orders/state/lidarMachine";
import { GeometryMachineContext } from "components/Orders/state/geometryMachine";

interface AddFilesButtonsProps {
  imagesDropzone: DropzoneState;
  lidarDropzone: DropzoneState;
  geometryDropzone: DropzoneState;
}

function AddFilesButtons(props: AddFilesButtonsProps) {
  return (
    <div
      css={css`
        padding: 1rem;
        border-top: 1px solid var(--neutral-5);
      `}
    >
      <ButtonGroup fill>
        <Tooltip content="Valid formats: .jpeg, .rjpeg" placement="bottom">
          <Button
            icon="media"
            size="large"
            {...props.imagesDropzone.getRootProps()}
          >
            <input {...props.imagesDropzone.getInputProps()} />
            <span>Add Images</span>
          </Button>
        </Tooltip>
        <Tooltip content="Valid formats: .las" placement="bottom">
          <Button
            icon="antenna"
            size="large"
            {...props.lidarDropzone.getRootProps()}
          >
            <input {...props.lidarDropzone.getInputProps()} />
            <span>Add Point Clouds</span>
          </Button>
        </Tooltip>
        <Tooltip
          content={
            <div
              css={css`
                max-width: 420px;
              `}
            >
              Valid formats: .kml/.kmz, .geojson, .shp (with accompanying .dbf,
              .prj, .shx, at minimum), .zip, .csv, .xlsx, .pdf
            </div>
          }
          placement="bottom"
          css={css`
            max-width: 500px;
          `}
        >
          <Button
            icon="shapes"
            size="large"
            {...props.geometryDropzone.getRootProps()}
          >
            <input {...props.geometryDropzone.getInputProps()} />
            <span>Add Layers and Accessories</span>
          </Button>
        </Tooltip>
      </ButtonGroup>
    </div>
  );
}

interface UploadDropzoneProps {
  dropzone: DropzoneState;
}

function UploadDropzone(props: UploadDropzoneProps) {
  const imageUploads = ImagesMachineContext.useSelector(
    (state) => state.context.uploads
  );
  const lidarUploads = LidarMachineContext.useSelector(
    (state) => state.context.uploads
  );
  const geometryUploads = GeometryMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const full =
    imageUploads.length === 0 &&
    lidarUploads.length === 0 &&
    geometryUploads.length === 0;

  return (
    <div
      {...props.dropzone.getRootProps()}
      css={css`
        transition: all 300ms ease-out;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border: 1px dashed var(--fybr-blue);
        border-radius: 0.5rem;
        padding: ${full ? "4rem" : "1rem"};
        height: 100%;
        box-shadow: 0 0 8px 4px var(--fybr-blue-translucent),
          inset 0 0 16px 4px var(--fybr-blue-translucent);

        &:hover {
          cursor: pointer;

          box-shadow: 0 0 8px 4px var(--fybr-blue),
            inset 0 0 64px ${full ? "4px" : "2px"} var(--fybr-blue);

          & [data-state="closed"] {
            display: none;
          }

          & [data-state="open"] {
            display: block;
          }
        }
      `}
    >
      <input {...props.dropzone.getInputProps()} />
      {full ? (
        <>
          <div
            data-state="closed"
            css={css`
              margin-bottom: 1rem;
            `}
          >
            <Icon icon="folder-close" size={96} color="var(--neutral-6)" />
          </div>
          <div
            data-state="open"
            css={css`
              display: none;
              margin-bottom: 1rem;
            `}
          >
            <Icon icon="folder-open" size={96} color="var(--neutral-4)" />
          </div>
          <h1
            css={css`
              color: var(--neutral-3);
              margin-bottom: 1rem;
              text-align: center;
              font-weight: 300;
            `}
          >
            Drop required order{" "}
            <strong
              css={css`
                font-weight: 700;
                color: var(--neutral-1);
              `}
            >
              folders
            </strong>{" "}
            or{" "}
            <strong
              css={css`
                font-weight: 700;
                color: var(--neutral-1);
              `}
            >
              files
            </strong>{" "}
            here
          </h1>
          <h4
            css={css`
              color: var(--neutral-4);
              text-align: center;
              font-weight: 400;
              max-width: 476px;
            `}
          >
            These can include a combination of images, point clouds, layers and
            accessories.
          </h4>
        </>
      ) : (
        <>
          <h4
            css={css`
              color: var(--neutral-3);
              text-align: center;
              font-weight: 400;
              max-width: 476px;
            `}
          >
            Drop required order{" "}
            <span
              css={css`
                font-weight: 700;
                color: var(--neutral-1);
              `}
            >
              folders
            </span>{" "}
            or{" "}
            <span
              css={css`
                font-weight: 700;
                color: var(--neutral-1);
              `}
            >
              files
            </span>{" "}
            here
          </h4>
          <p
            css={css`
              color: var(--neutral-4);
              text-align: center;
            `}
          >
            These can include a combination of images, point clouds, layers and
            accessories.
          </p>
        </>
      )}
    </div>
  );
}

interface UploadControlsProps {
  imagesDropzone: DropzoneState;
  lidarDropzone: DropzoneState;
  geometryDropzone: DropzoneState;
  dropzone: DropzoneState;
  showDropzone: boolean;
}

export default function UploadControls(props: UploadControlsProps) {
  return (
    <>
      <AddFilesButtons
        imagesDropzone={props.imagesDropzone}
        lidarDropzone={props.lidarDropzone}
        geometryDropzone={props.geometryDropzone}
      />
      <div
        css={css`
          flex: ${props.showDropzone ? 1 : 0};
          padding: 0 1rem 1rem 1rem;
        `}
      >
        <UploadDropzone dropzone={props.dropzone} />
      </div>
    </>
  );
}
